import { Alert, PageHeader } from "@justworkshr/milo-core";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useInviteEorMutation } from "types/generated/operations";
import { SupportedCountriesContext } from "../contexts/supportedCountriesContext";
import styles from "./InvitePage.module.css";
import Form, {
  COUNTRY_EXTENDED_CONFIG,
  SchemaType,
  isCountryExtendedConfig,
  type InviteFormProps,
} from "./form";
import { ContractTerm, EmailTypes, PayBasis, type AlertState } from "./types";
import {
  convertNumberToPennies,
  filterObject,
  scrollToTop,
  today,
} from "./utils";

const initialValues = (workCountry: string): SchemaType => ({
  firstName: "",
  lastName: "",
  homeEmail: "",
  workEmail: "",
  contractTerm:
    workCountry === "FR" || workCountry === "DE"
      ? ContractTerm.Fixed
      : ContractTerm.Indefinite,
  jobTitle: "",
  workCountry: "",
  payBasis: PayBasis.Salary,
  sendInvitationTo: EmailTypes.Work,
  startDate: "",
  honoredStartDate: "",
  payRate: "",
  managerUuid: "",
  departmentUuid: "",
  roleResponsibilities: "",
});

export default function InvitePage() {
  const { t } = useTranslation();
  const [inviteEorMutation, { loading }] = useInviteEorMutation();

  const [alert, setAlert] = useState<AlertState>();
  const { supportedCountriesData } = useContext(SupportedCountriesContext);

  const handleFormSubmit: InviteFormProps["onFormSubmit"] = (
    { sendInvitationTo, ...formValues },
    formActions
  ) => {
    const sendInviteTo =
      sendInvitationTo === EmailTypes.Home
        ? formValues.homeEmail ?? formValues.workEmail
        : formValues.workEmail;

    const currency = () => {
      const currencies =
        supportedCountriesData[formValues.workCountry]?.currencies;

      return currencies != null ? currencies[0].currencyCode : "";
    };

    const buildCountryDTO = () => {
      const workCountry = formValues.workCountry;
      const countryConfig = isCountryExtendedConfig(workCountry)
        ? COUNTRY_EXTENDED_CONFIG[workCountry]
        : undefined;

      if (countryConfig?.initialValues) {
        const keysToExclude = countryConfig.keysToExclude;
        const filteredEntries = Object.entries(formValues).filter(
          ([key]) => key in countryConfig.initialValues
        );
        const countryValues = Object.fromEntries(filteredEntries);

        return keysToExclude
          ? filterObject(countryValues, keysToExclude)
          : countryValues;
      }
    };

    const filteredBaseEntries = Object.entries(formValues).filter(
      ([key]) => key in initialValues(formValues.workCountry)
    );

    const baseValues = Object.fromEntries(filteredBaseEntries) as ReturnType<
      typeof initialValues
    >;

    const handleOnCompleted = () => {
      formActions.resetForm();
      setAlert({
        visible: true,
        message: t(
          "Thanks! Our team will review your request to add {{employeeName}} and get back to you with any next steps.",
          {
            employeeName: `${formValues.firstName} ${formValues.lastName}`,
          }
        ),
        type: "additive",
      });
      scrollToTop();
    };

    const handleOnError = () => {
      setAlert({
        visible: true,
        message: t("An error occurred while sending the invitation"),
        type: "destructive",
      });
      scrollToTop();
    };

    if (baseValues.honoredStartDate?.length === 0) {
      baseValues.honoredStartDate = undefined;
    }

    inviteEorMutation({
      variables: {
        formDetails: {
          ...baseValues,
          currency: currency(),
          payRate: convertNumberToPennies(formValues.payRate),
          sendInviteAt: today,
          sendInviteTo,
          ...buildCountryDTO(),
        },
      },
      onCompleted: handleOnCompleted,
      onError: handleOnError,
    });
  };

  return (
    <div className={styles.pageContainer}>
      {alert && (
        <Alert
          className={styles.alert}
          color={alert.type}
          dismissible
          visible={alert.visible}
        >
          {alert.message}
        </Alert>
      )}

      <PageHeader
        title={t("Add person")}
        linkPrevious={
          <Link to="/invitation-center">{t("Back to Invitation Center")}</Link>
        }
      />
      <Form
        onFormSubmit={handleFormSubmit}
        isLoading={loading}
        initialValues={initialValues("")}
      />
    </div>
  );
}
